import {
  CaretDownIcon,
  PencilSimpleIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import type { ICellRendererParams } from "ag-grid-community";
import { Button } from "@/components/ui/button";
import { Menu, MenuItem, MenuPopover, MenuTrigger } from "@/components/ui/menu";
import {
  openCreateNewsAlertModal,
  openDeleteNewsAlertModal,
  setDeleteNewsAlertId,
  setEditNewsAlertId,
  setNewsAlertName,
} from "@/store/news-alerts/news-alerts.actions";
import type { TableAlert } from "@/store/news-alerts/news-alerts.slice";
import { fetchNewsAlertById } from "@/store/news-alerts/news-alerts-async.actions";
import useScheduledBriefingStore from "@/store/news-alerts/scheduled-briefing/scheduled-briefing.slice";

interface NewsAlertsTableActionsProps {
  params: ICellRendererParams<TableAlert>;
  isFetching: boolean;
}

const NewsAlertsTableActions = ({
  params,
  isFetching,
}: NewsAlertsTableActionsProps) => {
  const openScheduledBriefingForEdit = useScheduledBriefingStore(
    (state) => state.openScheduledBriefingForEdit,
  );
  const openDeleteScheduledBriefingModal = useScheduledBriefingStore(
    (state) => state.openDeleteScheduledBriefingModal,
  );

  if (isFetching) {
    return null;
  }

  const columnWidth = params.column?.getActualWidth();
  const shouldHideIcon = columnWidth && columnWidth < 100;

  const handleEdit = async () => {
    const data = params.node.data;
    if (!data) return;

    if (data.scheduleType === "SCHEDULED_BRIEFING") {
      await openScheduledBriefingForEdit(data.id);
      return;
    }

    setEditNewsAlertId(data.id);
    const success = await fetchNewsAlertById(data.id);
    if (success) {
      openCreateNewsAlertModal();
    }
  };

  const handleDelete = () => {
    const data = params.node.data;
    if (!data) return;

    if (data.scheduleType === "SCHEDULED_BRIEFING") {
      openDeleteScheduledBriefingModal(data.id, data.name);
      return;
    }

    setNewsAlertName(data.name);
    setDeleteNewsAlertId(data.id);
    openDeleteNewsAlertModal();
  };

  return (
    <div className="flex flex-row gap-2 items-center min-w-0">
      <MenuTrigger>
        <Button
          variant="none"
          size="sm"
          className="gap-1 px-2 flex items-center"
          aria-label="More options"
        >
          <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs">
            Actions
          </span>
          {!shouldHideIcon && (
            <CaretDownIcon
              size={12}
              weight="fill"
              className="min-w-3 min-h-3 shrink-0"
            />
          )}
        </Button>
        <MenuPopover placement="bottom" offset={0}>
          <Menu>
            <MenuItem
              key="scheduler-item-edit"
              onAction={handleEdit}
              className="text-xs"
            >
              <PencilSimpleIcon size={14} />
              Edit
            </MenuItem>
            <MenuItem
              key="scheduler-item-delete"
              onAction={handleDelete}
              className="text-xs"
            >
              <TrashIcon size={14} className="text-red-600" />
              Delete
            </MenuItem>
          </Menu>
        </MenuPopover>
      </MenuTrigger>
    </div>
  );
};

export default NewsAlertsTableActions;
